import jsPDF from 'jspdf';
import ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';
import { PF, calibriWorkbook } from './tagFont';

// Dashboard widget export. data = { title, sub, head: [..], rows: [[..]], foot?: [..] }; node = chart area to picture (optional).
const NAVY = [30, 42, 74]; const GREEN = [15, 107, 79]; const MUTED = [100, 116, 139];
const today = () => new Date().toLocaleDateString('en-GB');
const fileName = (t) => `${String(t || 'Widget').replace(/[\\/:*?"<>|]+/g, ' ').trim()} - ${today().replace(/\//g, '-')}`;

async function picture(node) {
  if (!node) return null;
  const bg = getComputedStyle(node.closest('section') || node).backgroundColor;
  const c = await html2canvas(node, { scale: 2, backgroundColor: bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '#ffffff', logging: false, useCORS: true, ignoreElements: (el) => el.dataset?.exportIgnore === 'true',
    // Freeze entrance animations and un-clip truncated labels in the copy html2canvas draws.
    onclone: (doc) => { const st = doc.createElement('style'); st.textContent = '*,*::before,*::after{animation:none!important;transition:none!important}.truncate{overflow:visible!important;text-overflow:clip!important}.font-mono{letter-spacing:0!important;font-variant-numeric:normal!important}'; doc.head.appendChild(st); } });
  return { url: c.toDataURL('image/png'), w: c.width, h: c.height };
}

// "Rs. 1,23,456" / "12.345 g" / "3.20 ct" / "45%" / "1,234" -> number + Excel format; anything else stays text.
function cellOf(v) {
  if (typeof v === 'number') return { v, z: Number.isInteger(v) ? '#,##0' : '#,##0.00' };
  const s = String(v ?? '');
  const num = (x) => Number(x.replace(/,/g, ''));
  let m = s.match(/^Rs\.\s?(-?[\d,]+(?:\.\d+)?)$/); if (m) return { v: num(m[1]), z: '[>=10000000]"Rs. "##\\,##\\,##\\,##0;[>=100000]"Rs. "##\\,##\\,##0;"Rs. "##,##0' };
  m = s.match(/^(-?[\d,]+\.(\d+)) (g|ct)$/); if (m) return { v: num(m[1]), z: `0.${'0'.repeat(m[2].length)} "${m[3]}"` };
  m = s.match(/^(-?\d+(?:\.(\d+))?)%$/); if (m) return { v: num(m[1]) / 100, z: m[2] ? `0.${'0'.repeat(m[2].length)}%` : '0%' };
  m = s.match(/^-?[\d,]+(\.\d+)?$/); if (m && !/^0\d/.test(s)) return { v: num(s), z: m[1] ? `#,##0.${'0'.repeat(m[1].length - 1)}` : '#,##0' };
  return { v: s };
}

export async function exportWidgetExcel(data, node) {
  const pic = await picture(node);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(String(data.title || 'Widget').slice(0, 31).replace(/[[\]*?/\\:]/g, ' '), { views: [{ showGridLines: false }] });
  const n = data.head.length;
  const thin = { style: 'thin', color: { argb: 'FFCBD5E1' } };
  const box = { top: thin, left: thin, bottom: thin, right: thin };
  if (n > 1) { ws.mergeCells(1, 1, 1, n); ws.mergeCells(2, 1, 2, n); }
  ws.getCell(1, 1).value = data.title; ws.getCell(1, 1).font = { bold: true, size: 14, color: { argb: 'FF1E2A4A' } };
  ws.getCell(2, 1).value = [data.sub, `Generated ${today()}`].filter(Boolean).join('  ·  '); ws.getCell(2, 1).font = { size: 9, color: { argb: 'FF64748B' } };
  const hr = ws.getRow(4);
  data.head.forEach((h, i) => { const c = hr.getCell(i + 1); c.value = h; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E2A4A' } }; c.border = box; c.alignment = { horizontal: i < 2 ? 'left' : 'right', vertical: 'middle', wrapText: true }; });
  hr.height = 22;
  const put = (r, vals, bold) => vals.forEach((v, i) => {
    const c = ws.getRow(r).getCell(i + 1); const x = cellOf(v);
    c.value = x.v; if (x.z) c.numFmt = x.z; c.border = box;
    c.font = { bold: bold || i === 1, color: { argb: bold ? 'FF0F6B4F' : 'FF1E2A4A' } };
    c.alignment = { horizontal: typeof x.v === 'number' && i > 0 ? 'right' : 'left', vertical: 'middle' };
    if (bold) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } };
  });
  data.rows.forEach((r, i) => put(5 + i, r));
  if (data.foot) put(5 + data.rows.length, data.foot, true);
  data.head.forEach((h, i) => { const len = Math.max(String(h).length, ...data.rows.map((r) => String(r[i] ?? '').length), String(data.foot?.[i] ?? '').length); ws.getColumn(i + 1).width = Math.min(42, Math.max(i === 0 && h === '#' ? 5 : 10, len + 3)); });
  if (pic) {
    const id = wb.addImage({ base64: pic.url, extension: 'png' });
    const w = Math.min(720, pic.w / 2); ws.addImage(id, { tl: { col: n + 1, row: 3 }, ext: { width: w, height: (pic.h / pic.w) * w } });
  }
  const buf = await calibriWorkbook(wb).xlsx.writeBuffer();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  a.download = `${fileName(data.title)}.xlsx`; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

export async function exportWidgetPdf(data, node) {
  const pic = await picture(node);
  const land = data.head.length > 6;
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: land ? 'landscape' : 'portrait', compress: true }); pdf.setFont(PF());
  const W = land ? 297 : 210; const H = land ? 210 : 297; const MX = 12; const bw = W - 2 * MX;
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(15); pdf.setTextColor(...NAVY); pdf.text(String(data.title), MX, 16);
  pdf.setFont(PF(), 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(...MUTED);
  pdf.text(pdf.splitTextToSize([data.sub, `Generated ${today()}`].filter(Boolean).join('  ·  '), bw).slice(0, 2), MX, 22);
  pdf.setDrawColor(...GREEN); pdf.setLineWidth(0.6); pdf.line(MX, 26, W - MX, 26);
  let y = 31;
  if (pic) {
    let w = bw; let h = (pic.h / pic.w) * w;
    if (h > H * 0.55) { h = H * 0.55; w = (pic.w / pic.h) * h; }
    pdf.addImage(pic.url, 'PNG', MX + (bw - w) / 2, y, w, h, undefined, 'FAST'); y += h + 6;
  }
  const wt = data.head.map((h, i) => (h === '#' ? 0.35 : i === 1 || (i === 0 && h !== '#') ? 2 : 1));
  const unit = bw / wt.reduce((a, b) => a + b, 0); const ws = wt.map((x) => x * unit);
  const xs = ws.map((_, i) => MX + ws.slice(0, i).reduce((a, b) => a + b, 0));
  const RH = 6.6; const HR = 8;
  const fit = (t, w, size) => { let f = size; pdf.setFontSize(f); while (f > 5 && pdf.getTextWidth(t) > w) { f -= 0.3; pdf.setFontSize(f); } return t; };
  const right = (i) => i >= 2 || (i === 1 && data.head[0] !== '#');
  const header = () => {
    pdf.setFillColor(...NAVY); pdf.rect(MX, y, bw, HR, 'F');
    pdf.setFont(PF(), 'bold'); pdf.setTextColor(255, 255, 255);
    data.head.forEach((h, i) => pdf.text(fit(String(h).toUpperCase(), ws[i] - 3, 7.5), right(i) ? xs[i] + ws[i] - 1.5 : xs[i] + 1.5, y + 5.2, { align: right(i) ? 'right' : 'left' }));
    y += HR;
  };
  if (y + HR + RH > H - 12) { pdf.addPage('a4', land ? 'landscape' : 'portrait'); y = 14; }
  header();
  const line = (vals, k, bold) => {
    if (y + RH > H - 12) { pdf.addPage('a4', land ? 'landscape' : 'portrait'); y = 14; header(); }
    if (bold) { pdf.setFillColor(236, 253, 245); pdf.rect(MX, y, bw, RH, 'F'); } else if (k % 2) { pdf.setFillColor(248, 250, 252); pdf.rect(MX, y, bw, RH, 'F'); }
    pdf.setDrawColor(226, 232, 240); pdf.setLineWidth(0.2); pdf.line(MX, y + RH, W - MX, y + RH);
    vals.forEach((v, i) => {
      pdf.setFont(PF(), bold || i === 1 ? 'bold' : 'normal'); pdf.setTextColor(...(bold ? GREEN : i === 0 && data.head[0] === '#' ? MUTED : NAVY));
      pdf.text(fit(String(v ?? ''), ws[i] - 3, 8.5), right(i) ? xs[i] + ws[i] - 1.5 : xs[i] + 1.5, y + 4.4, { align: right(i) ? 'right' : 'left' });
    });
    y += RH;
  };
  data.rows.forEach((r, k) => line(r, k));
  if (data.foot) line(data.foot, 0, true);
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) { pdf.setPage(p); pdf.setFont(PF(), 'normal'); pdf.setFontSize(7); pdf.setTextColor(...MUTED); pdf.text(`${data.title} · ${p} / ${pages}`, W - MX, H - 6, { align: 'right' }); }
  pdf.save(`${fileName(data.title)}.pdf`);
}
